import React from 'react'
import styled, { css } from 'styled-components'
import cn from 'classnames'
import { FormCustom } from '@bootstrap-styled/v4'
import { ifThen, ifElse } from '@bootstrap-styled/css-mixins'
import { AppColors } from '../../../typings/global'

export type CheckboxProps = {
    className?: string
    color?: AppColors
    checkType?: 'default' | 'radiobox'
} & Omit<React.ComponentProps<typeof FormCustom>, 'radio'>

const CheckboxUnstyled: React.FC<CheckboxProps> = ({
    children,
    className,
    onChange,
    color,
    checkType = 'default',
    ...props
}) => {
    const classes = cn(className, {
        [`custom-${checkType}`]: !!checkType,
        [`text-${color}`]: !!color
    })

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const target = e.target
        const parentClass = (target.parentNode as Element).classList

        if (target.checked) {
            parentClass.add('custom-control-checked')
        } else {
            parentClass.remove('custom-control-checked')
        }

        if (typeof onChange === 'function') {
            onChange(e)
        }
    }

    return (
        <FormCustom onChange={handleChange} className={classes} {...props}>
            {children}
        </FormCustom>
    )
}

/**
 * Checkbox component that allows a child text as a label
 */
const Checkbox = styled(CheckboxUnstyled)`
    & .custom-control-input {
        left: -90000px;
    }

    /* default checkbox customize */
    &.custom-default {
        && {
            padding-left: ${props => props.theme['$custom-control-indicator-size']};
            line-height: ${props => props.theme['$custom-control-indicator-size']};
            min-height: ${props => props.theme['$custom-control-indicator-size']};
            margin-bottom: 0px;
        }
        color: ${props => props.theme['$custom-control-label-color']};

        /* checkbox style */
        & .custom-control-indicator {
            font-size: ${props => props.theme['$custom-control-indicator-size']};
            pointer-events: none;
            user-select: none;
            box-shadow: ${props => props.theme['$custom-control-indicator-box-shadow']};
            display: block;
            position: absolute;
            top: 0px;
            left: 0px;
            width: 1em;
            height: 1em;
            border: ${props => props.theme['$custom-control-indicator-border-width']} solid
                ${props => props.theme['$custom-checkbox-indicator-border-color']};
            background-color: ${props => props.theme['$custom-control-indicator-bg']};
            transition: ${props => props.theme['$custom-forms-transition']};
            ${props =>
                ifThen(
                    props.theme['$enable-rounded'],
                    css`
                        border-radius: ${props.theme['$custom-checkbox-indicator-border-radius']};
                    `
                )}

            &:before {
                content: '';
                position: absolute;
                width: 0;
                height: 0;
                border: 3px solid transparent;
                right: 0.5em;
                bottom: 3px;
                transition: ${props => props.theme['$custom-forms-transition']};
                transform: rotateZ(37deg);
                transform-origin: 100% 100%;
            }
        }

        /* Styles base at checkbox state control */
        & .custom-control-input {
            &:checked {
                & ~ .custom-control-indicator {
                    color: ${props => props.theme['$custom-control-indicator-checked-color']};
                    background-color: ${props =>
                        props.theme['$custom-control-indicator-checked-bg']};
                    border-color: ${props =>
                        props.theme['$custom-control-indicator-checked-border-color']};

                    &:before {
                        width: 0.4em;
                        height: 0.7em;
                        background-color: ${props =>
                            props.theme['$custom-control-indicator-checked-bg']};
                        border-top: 2px solid transparent;
                        border-left: 2px solid transparent;
                        border-right: 2px solid
                            ${props => props.theme['$custom-control-indicator-checked-color']};
                        border-bottom: 2px solid
                            ${props => props.theme['$custom-control-indicator-checked-color']};
                        z-index: 1;
                    }
                }

                &:disabled {
                    & ~ .custom-control-indicator {
                        background-color: ${props =>
                            props.theme['$custom-control-indicator-checked-disabled-bg']};

                        &:before {
                            background-color: ${props =>
                                props.theme['$custom-control-indicator-disabled-bg']};
                            border-color: ${props => props.theme['$light-gray']};
                            border-top-color: transparent;
                            border-left-color: transparent;
                        }
                    }
                }
            }

            &:disabled {
                & ~ .custom-control-indicator {
                    background-color: ${props =>
                        props.theme['$custom-control-indicator-disabled-bg']};
                    border-color: ${props => props.theme['$gray-light']};
                }

                & ~ .custom-control-description {
                    color: ${props => props.theme['$custom-control-label-disabled-color']};
                }
            }

            &:focus {
                & ~ .custom-control-indicator {
                    box-shadow: ${props =>
                        props.theme['$custom-control-indicator-focus-box-shadow']};
                    border-color: ${props =>
                        props.theme['$custom-control-indicator-focus-border-color']};
                }

                &:checked {
                    & ~ .custom-control-indicator {
                        box-shadow: ${props =>
                            props.theme['$custom-control-indicator-checked-focus-box-shadow']};
                        border-color: ${props =>
                            props.theme['$custom-control-indicator-checked-focus-border-color']};

                        &:before {
                            border-color: ${props =>
                                props.theme[
                                    '$custom-control-indicator-checked-focus-border-color'
                                ]};
                            border-top-color: transparent;
                            border-left-color: transparent;
                        }
                    }
                }
            }
        }

        /* Styles for checkbox text label */
        & .custom-control-description {
            display: inline-block;
            margin-left: ${props => props.theme['$custom-control-gutter']};
            margin-bottom: 0;
            color: inherit;

            &:empty {
                display: none;
            }
        }

        /* when it's passed like a tag into DropdownToggle */
        &.dropdown-toggle {
            &.dropdown-toggle-split::after {
                margin-left: 0;
                margin-right: 0;
            }
        }
    }

    /* custom checkbox look as radio box */
    &.custom-radiobox {
        color: ${props => props.theme['$custom-control-label-color']};
        height: ${props => props.theme['$custom-radiobox-size']};
        min-width: ${props => props.theme['$custom-radiobox-size']};
        line-height: ${props => props.theme['$custom-radiobox-size']};
        text-align: center;
        padding: 0;

        /* radius style */
        & .custom-control-indicator {
            pointer-events: none;
            user-select: none;
            box-shadow: ${props => props.theme['$custom-control-indicator-box-shadow']};

            display: block;
            position: absolute;
            top: 0px;
            left: 0px;
            width: 100%;
            height: 100%;
            border: ${props => props.theme['$custom-control-indicator-border-width']} solid
                ${props => props.theme['$custom-control-indicator-border-color']};
            background-color: ${props => props.theme['$custom-control-indicator-bg']};
            transition: ${props => props.theme['$custom-forms-transition']};
            ${props =>
                ifElse(
                    props.theme['$enable-rounded'],
                    css`
                        border-radius: ${props.theme['$custom-checkbox-indicator-border-radius']};
                    `,
                    css`
                        border-radius: 0;
                    `
                )}
        }

        /* Styles based on radio state control */
        & .custom-control-input {
            &:checked {
                & ~ .custom-control-indicator {
                    background-color: ${props => props.theme['$custom-control-indicator-bg']};
                    border-color: ${props =>
                        props.theme['$custom-control-indicator-checked-border-color']};
                }

                & ~ .custom-control-description {
                    color: ${props => props.theme['$custom-radiobox-checked-color']};
                }
            }

            &:focus {
                & ~ .custom-control-indicator {
                    box-shadow: none;
                }
            }
        }

        & .custom-control-description {
            position: relative;
            width: 100%;
        }
    }
`

export default Checkbox
